<script setup>
import { layoutConfig } from '@layouts'
import { useLayoutConfigStore } from '@layouts/stores/config'
import {
    getComputedNavLinkToProp,
    getDynamicI18nProps,
    isNavLinkActive,
} from '@layouts/utils'
import { ref } from 'vue'

const props = defineProps({
    item: {
        type: null,
        required: true,
    },
})

const configStore = useLayoutConfigStore()
// Always show text labels
const hideTitleAndBadge = ref(false)
const loading = ref(false)

const handleWrapperClick = (event) => {
    // If no 'to'/'href' property but has popup function, trigger it
    if (
        !props.item.to &&
        !props.item.href &&
        props.item.popup &&
        typeof props.item.popup === 'function'
    ) {
        event.preventDefault()
        event.stopPropagation()
        props.item.popup()
    }
}

const handleAppendIconClick = async (event) => {
    // Prevent navigation when clicking append icon
    event.stopPropagation()
    event.preventDefault()

    // Call the append icon function if defined
    if (
        props.item.appendIconFn &&
        typeof props.item.appendIconFn === 'function'
    ) {
        loading.value = true
        try {
            await props.item.appendIconFn()
        } finally {
            loading.value = false
        }
    }
}
</script>

<template>
    <!-- v-if="can(item.action, item.subject)" -->
    <li
        class="nav-link"
        :class="{
            disabled: item.disable,
            'no-link': !item.to && !item.href,
            'has-popup': !item.to && !item.href && item.popup,
        }"
    >
        <Component
            :is="item.to ? 'RouterLink' : item.href ? 'a' : 'div'"
            v-bind="item.to || item.href ? getComputedNavLinkToProp(item) : {}"
            :class="{
                'router-link-active router-link-exact-active':
                    item.to && isNavLinkActive(item, $router),
                'nav-item-wrapper': true,
            }"
            @click="handleWrapperClick"
        >
            <VBadge
                :content="item.badgeContent"
                :class="item.badgeClass + ' nav-item-badge-collapsed'"
                location="top right"
                offset-x="1"
                offset-y="2"
                :model-value="item.badgeContent > 0 && hideTitleAndBadge"
            >
                <Component
                    :is="layoutConfig.app.iconRenderer || 'div'"
                    v-bind="
                        item.icon ||
                        layoutConfig.verticalNav.defaultNavItemIconProps
                    "
                    class="nav-item-icon"
                />
            </VBadge>

            <TransitionGroup name="transition-slide-x">
                <span
                    v-show="!hideTitleAndBadge"
                    key="title-and-badge-wrapper"
                    class="d-flex align-center"
                >
                    <Component
                        :is="layoutConfig.app.i18n.enable ? 'i18n-t' : 'span'"
                        class="nav-item-title"
                        v-bind="getDynamicI18nProps(item.title, 'span')"
                    >
                        {{ item.title }}
                    </Component>

                    <Component
                        :is="
                            layoutConfig.app.i18n.enable &&
                            isNaN(Number(item.badgeContent))
                                ? 'i18n-t'
                                : 'span'
                        "
                        v-if="item.badgeContent"
                        key="badge"
                        class="nav-item-badge"
                        :class="item.badgeClass"
                        v-bind="getDynamicI18nProps(item.badgeContent, 'span')"
                    >
                        {{ item.badgeContent }}
                    </Component>
                </span>

                <!-- Append icon with action (e.g., for Proxy unlink) -->
                <Component
                    v-if="item.appendIcon && !hideTitleAndBadge"
                    :is="layoutConfig.app.iconRenderer || 'div'"
                    :icon="item.appendIcon"
                    key="append-icon"
                    class="nav-item-append-icon"
                    :class="{
                        clickable: item.appendIconFn,
                        loading: loading,
                    }"
                    @click="
                        item.appendIconFn ? handleAppendIconClick($event) : null
                    "
                />
            </TransitionGroup>
        </Component>
    </li>
</template>

<style lang="scss">
.layout-vertical-nav {
    .nav-link {
        // Items with popup but no link
        &.has-popup {
            div.nav-item-wrapper {
                cursor: pointer !important;

                &:hover {
                    background-color: rgba(
                        var(--v-theme-on-surface),
                        var(--v-hover-opacity)
                    ) !important;
                }

                // Title color to match other menu items
                .nav-item-title {
                    color: rgb(var(--v-inactive-color)) !important;
                    font-weight: 500 !important;
                }

                // Allow append icon clicks to bubble through separately
                .nav-item-append-icon.clickable {
                    position: relative;
                    z-index: 1;
                }
            }
        }

        // Items with no link and no popup
        &.no-link:not(.has-popup) {
            div.nav-item-wrapper {
                cursor: default;

                &:hover {
                    background-color: transparent !important;
                }
            }
        }
    }

    .nav-link a,
    .nav-link div.nav-item-wrapper {
        display: flex;
        align-items: center;
        padding-top: 12px !important;
        padding-bottom: 12px !important;
    }

    .nav-item-badge {
        margin-left: 8px;
    }

    .nav-item-badge-collapsed .v-badge__badge {
        font-size: 10px;
        background: #ed151b !important;
        color: #ffffff !important;
    }

    .nav-item-append-icon {
        margin-left: auto;
        margin-right: 8px;

        &.clickable {
            cursor: pointer;
            transition: opacity 0.2s ease;

            &:hover {
                opacity: 0.7;
            }

            &.loading {
                opacity: 0.5;
                cursor: wait;
            }
        }
    }
}

// Force text to always show
.layout-vertical-nav {
    .nav-item-title,
    .nav-item-badge {
        display: block !important;
    }
}
</style>
